import { Button } from '@components/button';
import { buttonVariants } from '@components/button/variants';
import { Loader } from '@components/loader';
import { useCheckUserPermissions } from '@hooks/checkUserPermisions';
import { BaseLayout } from '@layouts/base';
import { DetailsCard, DetailsCardItem } from '@components/detailsCard';
import { useResetPassword } from '@services/staff/users/reset';
import { Link, createFileRoute, redirect, useNavigate, useParams } from '@tanstack/react-router';
import { Mail, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useGet } from '@services/staff/users/get';
import { Page404 } from '@pages/404';
import { PermissionsAccordion } from '@components/permissionsAccordion';
import { useGetPermissionGroups } from '@services/staff/users/permissionGroups';

const StaffUserPage = () => {
  const { t } = useTranslation('app');
  const { verifyUserPermission } = useCheckUserPermissions();
  const { id, localId } = useParams({ from: '/u/$localId/_staff/staff/users/$id/' });
  const navigate = useNavigate();
  const { data, isPending } = useGet(id);
  const { data: permissions } = useGetPermissionGroups();

  const { mutate, isPending: requested } = useResetPassword({
    id,
    callback: () => {
      toast.success(t('toast.password.requestSuccessful'));
    },
  });

  if (isPending) return <Loader />;
  if (!data) return <Page404 />;
  return (
    <BaseLayout
      isChild
      title={data.name}
      actions={[
        verifyUserPermission('staff_users_create') && (
          <Button
            key="duplicate"
            variant="outline"
            onClick={() => {
              navigate({
                to: '/u/$localId/staff/users/new',
                params: { localId },
                search: { duplicateFrom: data.id },
              }).catch((err) => console.error(err));
            }}>
            {t('actions.duplicate')}
          </Button>
        ),
        verifyUserPermission(`staff_users_password_reset`) && (
          <Button
            key="reset"
            variant="outline"
            loading={requested}
            disabled={requested}
            onClick={() => mutate()}>
            {t('passwordReset')}
          </Button>
        ),
        verifyUserPermission('staff_users_update') && (
          <Link
            key="edit"
            to="/u/$localId/staff/users/$id/edit"
            params={{ id: data.id, localId }}
            className={buttonVariants({ variant: 'primary' })}>
            {t('actions.edit')}
          </Link>
        ),
      ]}>
      <DetailsCard>
        <DetailsCardItem
          icon={<Mail size={26} />}
          label={t('table.email')}
          data={data.email}
          column={1}
        />
        <DetailsCardItem
          icon={<User size={26} />}
          label={t('table.name')}
          data={data.name}
          column={2}
        />
      </DetailsCard>
      {permissions && (
        <PermissionsAccordion permissions={permissions} selectedPermissions={data.permissions} />
      )}
    </BaseLayout>
  );
};

export const Route = createFileRoute('/u/$localId/_staff/staff/users/$id/')({
  beforeLoad: ({ context }) => {
    if (!context.userHasPermissions(['staff_users_read'])) {
      throw redirect({
        to: `/u/$localId/${context.userType}/forbidden`,
      });
    }
  },
  component: StaffUserPage,
});
